import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BottomNavigation as MuiBottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledBottomNavigation = styled(MuiBottomNavigation)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 10000,
  backgroundColor: 'background.paper',
  boxShadow: '0px -20px 100px 0px rgba(0, 0, 0, 0.1)',
}));

const StyledBottomNavigationAction = styled(BottomNavigationAction)(({ theme }) => ({
  '& .MuiBottomNavigationAction-label': {
    fontSize: '0.75rem',
    color: theme.palette.text.disabled,
    '&.Mui-selected': {
      fontSize: '0.75rem',
      color: theme.palette.primary.main,
    },
  },
}));

// SVG components
const HomeIcon = ({ isSelected }) => (
  <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M1.875 9.45222V14.8002C1.875 15.9203 1.875 16.4807 2.09299 16.9086C2.28473 17.2849 2.59047 17.5906 2.9668 17.7823C3.3942 18.0001 3.95399 18.0001 5.07191 18.0001H14.6781C15.796 18.0001 16.355 18.0001 16.7824 17.7823C17.1587 17.5906 17.4655 17.2849 17.6572 16.9086C17.875 16.4811 17.875 15.9216 17.875 14.8037V9.45222C17.875 8.91789 17.8745 8.65057 17.8096 8.40193C17.752 8.18159 17.6575 7.97307 17.5296 7.78464C17.3852 7.57201 17.1846 7.39569 16.7824 7.04383L11.9824 2.84383C11.2358 2.19054 10.8625 1.86406 10.4424 1.73982C10.0722 1.63035 9.67761 1.63035 9.30742 1.73982C8.88761 1.86397 8.51485 2.19014 7.76936 2.84244L2.96777 7.04383C2.56564 7.39569 2.36504 7.57201 2.2207 7.78464C2.09279 7.97307 1.99755 8.18159 1.93997 8.40193C1.875 8.65057 1.875 8.91788 1.875 9.45222Z"
      stroke={isSelected ? '#215F4B' : '#D9D9D9'} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const LikeIcon = ({ isSelected }) => (
  <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M10.625 4.69431C8.625 -0.000122309 1.625 0.499878 1.625 6.49991C1.625 12.4999 10.625 17.5001 10.625 17.5001C10.625 17.5001 19.625 12.4999 19.625 6.49991C19.625 0.499878 12.625 -0.000122309 10.625 4.69431Z"
      stroke={isSelected ? '#215F4B' : '#D9D9D9'} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const PlayIcon = ({ isSelected }) => (
  <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M18.375 5H16.2236C15.7261 5 15.375 4.49751 15.375 4C15.375 2.34315 14.0319 1 12.375 1C10.7181 1 9.375 2.34315 9.375 4C9.375 4.49751 9.02384 5 8.52634 5H6.375C5.82271 5 5.375 5.44772 5.375 6V8.15133C5.375 8.64884 4.87251 9 4.375 9C2.71815 9 1.375 10.3431 1.375 12C1.375 13.6569 2.71815 15 4.375 15C4.87251 15 5.375 15.3511 5.375 15.8486V18C5.375 18.5523 5.82271 19 6.375 19L18.375 19C18.9273 19 19.375 18.5523 19.375 18V15.8486C19.375 15.3511 18.8725 15 18.375 15C16.7181 15 15.375 13.6569 15.375 12C15.375 10.3431 16.7181 9 18.375 9C18.8725 9 19.375 8.64884 19.375 8.15134L19.375 6C19.375 5.44772 18.9273 5 18.375 5Z"
      stroke={isSelected ? '#215F4B' : '#D9D9D9'} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const SettingsIcon = ({ isSelected }) => (
  <svg width="23" height="20" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.4749 7.92293L19.1087 7.7192C19.0519 7.68756 19.0239 7.67169 18.9964 7.65524C18.7233 7.49165 18.4932 7.26564 18.3252 6.99523C18.3083 6.96802 18.2924 6.93949 18.2598 6.8831C18.2273 6.82677 18.2108 6.79823 18.1956 6.76998C18.045 6.48866 17.9635 6.17515 17.9586 5.85606C17.9581 5.82398 17.9582 5.79121 17.9593 5.72604L17.9665 5.30078C17.9779 4.62025 17.9837 4.27894 17.888 3.97262C17.8031 3.70053 17.661 3.44993 17.4712 3.23725C17.2567 2.99685 16.9597 2.82534 16.3652 2.48276L15.8714 2.1982C15.2786 1.85658 14.9821 1.68571 14.6673 1.62057C14.3889 1.56294 14.1015 1.56561 13.8241 1.62789C13.5109 1.69819 13.2181 1.87351 12.6329 2.22396L12.6295 2.22555L12.2757 2.43741C12.2198 2.47091 12.1915 2.48779 12.1634 2.50338C11.8851 2.6581 11.5745 2.74365 11.2562 2.75387C11.2242 2.7549 11.1915 2.7549 11.1263 2.7549C11.0615 2.7549 11.0274 2.7549 10.9954 2.75387C10.6765 2.74361 10.3652 2.65759 10.0865 2.50224C10.0584 2.48658 10.0306 2.46956 9.97457 2.4359L9.61849 2.22213C9.02922 1.86836 8.73415 1.69121 8.41927 1.62057C8.1407 1.55807 7.85237 1.55634 7.57291 1.61471C7.25736 1.68062 6.96077 1.85276 6.36758 2.19703L6.36494 2.1982L5.87728 2.48124L5.87188 2.48454C5.28404 2.82572 4.98941 2.99672 4.7767 3.23614C4.58794 3.4486 4.44685 3.69881 4.3624 3.97018C4.26694 4.27691 4.27203 4.61896 4.28353 5.3027L4.29068 5.72736C4.29176 5.79166 4.29364 5.82362 4.29317 5.85525C4.28843 6.17499 4.20586 6.48914 4.05474 6.77096C4.0398 6.79883 4.0237 6.8267 3.99154 6.88237C3.95936 6.93809 3.94377 6.96579 3.92709 6.99268C3.75836 7.26452 3.52714 7.49186 3.25233 7.65572C3.22515 7.67193 3.1965 7.68752 3.14021 7.71871L2.77865 7.91908C2.17708 8.25245 1.87637 8.41928 1.65756 8.65669C1.46398 8.86672 1.31775 9.11585 1.22849 9.38717C1.12759 9.69387 1.12767 10.0378 1.12924 10.7255L1.13051 11.2877C1.13206 11.9708 1.13419 12.3122 1.23532 12.6168C1.32479 12.8863 1.46995 13.134 1.66244 13.3427C1.88002 13.5787 2.17774 13.7445 2.77474 14.0766L3.13308 14.276C3.19407 14.3099 3.22476 14.3266 3.25417 14.3444C3.52648 14.5083 3.75589 14.735 3.92318 15.0053C3.94125 15.0345 3.9586 15.0648 3.9933 15.1255C4.02756 15.1853 4.04509 15.2152 4.06094 15.2452C4.20761 15.5229 4.28614 15.8315 4.29149 16.1455C4.29207 16.1794 4.29158 16.2137 4.29041 16.2827L4.28353 16.6902C4.27195 17.3763 4.2669 17.7197 4.36292 18.0273C4.44787 18.2994 4.58984 18.55 4.77963 18.7627C4.99415 19.0031 5.29155 19.1745 5.88607 19.5171L6.37978 19.8015C6.97263 20.1432 7.26895 20.3138 7.58369 20.379C7.86214 20.4366 8.14964 20.4344 8.42709 20.3721C8.74067 20.3017 9.03448 20.1258 9.62142 19.7743L9.97525 19.5625C10.0312 19.5289 10.0596 19.5121 10.0876 19.4965C10.3659 19.3418 10.6762 19.2558 10.9945 19.2456C11.0265 19.2446 11.0592 19.2446 11.1244 19.2446C11.1898 19.2446 11.2224 19.2446 11.2545 19.2456C11.5734 19.2559 11.8857 19.3422 12.1644 19.4975C12.1889 19.5112 12.2135 19.526 12.2566 19.5519L12.6328 19.7777C13.2221 20.1315 13.5166 20.3081 13.8315 20.3788C14.11 20.4413 14.3986 20.4438 14.6781 20.3855C14.9935 20.3196 15.2907 20.1471 15.8836 19.803L16.3786 19.5157C16.9668 19.1743 17.2617 19.0031 17.4745 18.7636C17.6633 18.5512 17.8046 18.3011 17.889 18.0297C17.9838 17.7252 17.9781 17.3858 17.9667 16.7119L17.9593 16.2724C17.9582 16.2081 17.9581 16.1761 17.9586 16.1445C17.9633 15.8247 18.0445 15.5104 18.1956 15.2286C18.2106 15.2007 18.2268 15.1726 18.2588 15.1171C18.291 15.0615 18.3077 15.0337 18.3244 15.0068C18.4931 14.7349 18.7245 14.5074 18.9994 14.3435C19.0262 14.3275 19.0539 14.3122 19.1088 14.2818L19.1107 14.2809L19.4722 14.0805C20.0738 13.7472 20.3751 13.5801 20.5939 13.3427C20.7875 13.1327 20.9335 12.8839 21.0228 12.6126C21.1231 12.3077 21.1223 11.9658 21.1208 11.2861L21.1195 10.7119C21.1179 10.0287 21.1171 9.6874 21.016 9.38277C20.9265 9.11328 20.7805 8.86561 20.588 8.65685C20.3707 8.42111 20.0725 8.25526 19.4767 7.92378L19.4749 7.92293Z" stroke={isSelected ? '#215F4B' : '#D9D9D9'} stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path 
      d="M7.12533 11C7.12533 13.2091 8.91619 15 11.1253 15C13.3345 15 15.1253 13.2091 15.1253 11C15.1253 8.79082 13.3345 6.99996 11.1253 6.99996C8.91619 6.99996 7.12533 8.79082 7.12533 11Z"
      stroke={isSelected ? '#215F4B' : '#D9D9D9'} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (event, newValue) => {
    navigate(newValue);
  };

  return (
    <StyledBottomNavigation
      value={location.pathname}
      onChange={handleChange}
      showLabels
    >
      <StyledBottomNavigationAction
        label="Home"
        value="/"
        icon={<HomeIcon isSelected={location.pathname === '/'} />}
      />
      <StyledBottomNavigationAction
        label="Likes"
        value="/likes"
        icon={<LikeIcon isSelected={location.pathname === '/likes'} />}
      />
      <StyledBottomNavigationAction
        label="Play"
        value="/play"
        icon={<PlayIcon isSelected={location.pathname === '/play'} />}
      />
      <StyledBottomNavigationAction
        label="Settings"
        value="/settings"
        icon={<SettingsIcon isSelected={location.pathname === '/settings'} />}
      />
    </StyledBottomNavigation>
  );
};

export default BottomNavigation; 